package meineglasfaser.frontend.common.components.widgets

import androidx.compose.runtime.*
import kotlinx.coroutines.*
import org.jetbrains.compose.web.dom.*
import kotlin.time.Clock
import kotlin.time.Duration
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Instant

private val TOAST_DEFAULT_DURATION = 4000.milliseconds
private val TOAST_FADE_OUT_DURATION = 400.milliseconds

enum class ToastType {
    INFO,
    WARNING,
    ERROR,
    SUCCESS;
}

@ConsistentCopyVisibility
data class Toast private constructor(
    val type: ToastType,
    val title: String,
    val message: String?,
    val createdAt: Instant,
    val duration: Duration,
) {
    val id: String = "${createdAt}-${(0..9999).random()}"

    companion object {
        fun info(
            title: String,
            message: String? = null,
            createdAt: Instant = Clock.System.now(),
            duration: Duration = TOAST_DEFAULT_DURATION,
        ) = Toast(ToastType.INFO, title, message, createdAt, duration)

        fun warning(
            title: String,
            message: String? = null,
            createdAt: Instant = Clock.System.now(),
            duration: Duration = TOAST_DEFAULT_DURATION,
        ) = Toast(ToastType.WARNING, title, message, createdAt, duration)

        fun error(
            title: String,
            message: String? = null,
            createdAt: Instant = Clock.System.now(),
            duration: Duration = TOAST_DEFAULT_DURATION,
        ) = Toast(ToastType.ERROR, title, message, createdAt, duration)

        fun success(
            title: String,
            message: String? = null,
            createdAt: Instant = Clock.System.now(),
            duration: Duration = TOAST_DEFAULT_DURATION,
        ) = Toast(ToastType.SUCCESS, title, message, createdAt, duration)
    }
}

object Toasts {
    private val scope = CoroutineScope(Dispatchers.Default)
    private val _toasts = mutableStateListOf<Toast>()
    val toasts: List<Toast> get() = _toasts

    fun display(toast: Toast) {
        _toasts.add(toast)
        scope.launch {
            delay(toast.duration)
            remove(toast.id)
        }
    }

    fun remove(id: String) {
        _toasts.removeAll { it.id == id }
    }
}

@Composable
fun ToastContainer() {
    Div(
        attrs = {
            classes("toast-container")
        }
    ) {
        for (toast in Toasts.toasts) {
            key(toast.id) {
                ToastItem(toast)
            }
        }
    }
}

@Composable
fun ToastItem(toast: Toast) {
    var fadeOut by remember { mutableStateOf(false) }

    LaunchedEffect(toast.id) {
        delay(toast.duration - TOAST_FADE_OUT_DURATION)
        fadeOut = true
        delay(TOAST_FADE_OUT_DURATION)
        Toasts.remove(toast.id)
    }

    Div(
        attrs = {
            classes("toast", "toast-${toast.type.name.lowercase()}")
            if (fadeOut) {
                classes("fade-out")
            }
        }
    ) {
        Div {
            B { Text(toast.title) }
            toast.message?.let { msg ->
                P { Text(msg) }
            }
        }
        Button(
            attrs = {
                onClick {
                    Toasts.remove(toast.id)
                }
                classes("toast-close")
            }
        ) {
            BootstrapIcon("x-lg")
        }
    }
}
